import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { parseDateString } from '../lib/utils';
import Navbar from '../components/Navbar';

// Interface definitions
interface AgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
  birth_date_min?: string;
  birth_date_max?: string;
  created_at: string;
}

interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string | null;
  location_address: string | null;
  created_at: string;
}

interface TryoutLocation {
  name: string;
  address: string;
}

interface TryoutSession {
  id: string;
  date: string;
  time: string;
  location: TryoutLocation;
  notes?: string;
}

const AgeGroupTryoutInfo: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ageGroupId = searchParams.get('age_group_id');
  
  const [ageGroup, setAgeGroup] = useState<AgeGroup | null>(null);
  const [sessions, setSessions] = useState<TryoutSession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTryoutInfo = async () => {
      if (!ageGroupId) {
        setError('Age group ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Step 1: Fetch age group details
        const { data: ageGroupData, error: ageGroupError } = await supabase
          .from('age_groups')
          .select('*')
          .eq('id', ageGroupId)
          .single();
        
        if (ageGroupError) throw new Error(`Error fetching age group: ${ageGroupError.message}`);
        if (!ageGroupData) throw new Error('Age group not found');
        
        setAgeGroup(ageGroupData);
        
        // Step 2: Find session links for this age group
        const { data: sessionLinks, error: linksError } = await supabase
          .from('evaluation_session_age_groups')
          .select('evaluation_session_id')
          .eq('age_group_id', ageGroupId);
        
        if (linksError) throw new Error(`Error fetching session links: ${linksError.message}`);
        if (!sessionLinks || sessionLinks.length === 0) {
          setSessions([]);
          setLoading(false);
          return;
        }
        
        // Step 3: Fetch actual sessions
        const sessionIds = sessionLinks.map(link => link.evaluation_session_id);
        const { data: sessionData, error: sessionsError } = await supabase
          .from('evaluation_sessions')
          .select('*')
          .in('id', sessionIds)
          .order('session_date', { ascending: true });
        
        if (sessionsError) throw new Error(`Error fetching sessions: ${sessionsError.message}`);
        if (!sessionData) throw new Error('No sessions found');
        
        // Format the sessions for display
        const formattedSessions = sessionData.map(formatSession);
        setSessions(formattedSessions);
        
      } catch (error) {
        console.error('Error fetching tryout information:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchTryoutInfo();
  }, [ageGroupId]);

  // Helper function to format time strings
  const formatTimeString = (timeString: string): string => {
    try {
      // Parse the time string (expected format: 'HH:MM:SS')
      const [hours, minutes] = timeString.split(':').map(Number);
      
      // Create a date object to use the built-in formatting
      const date = new Date();
      date.setHours(hours, minutes, 0);
      
      // Format the time
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString; // Return the original string if there's an error
    }
  };

  // Helper function to format a session
  const formatSession = (session: EvaluationSession): TryoutSession => {
    // Format date
    const date = parseDateString(session.session_date);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    
    // Format time
    const startTime = formatTimeString(session.start_time);
    const endTime = formatTimeString(session.end_time);
    const timeRange = `${startTime} - ${endTime}`;
    
    return {
      id: session.id,
      date: formattedDate,
      time: timeRange,
      location: {
        name: session.location_name || 'TBD',
        address: session.location_address || 'Location to be determined'
      },
      notes: 'Please arrive 15 minutes early for check-in. Bring a ball, cleats, shin guards, and water.'
    };
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
          <img src="/cdo.png" alt="Club Development Organization" className="h-24 object-contain" />
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="flex items-center text-indigo-600 hover:text-indigo-800 transition">
              <ChevronLeft className="h-5 w-5 mr-1" />
              <span>Back to Home</span>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          ) : !ageGroup ? (
            <div className="bg-amber-100 border border-amber-400 text-amber-700 px-4 py-3 rounded relative mb-6" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">Age group not found</span>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-800">
                  {ageGroup.group_name} {ageGroup.sex === 'male' ? 'Boys' : 'Girls'} Placement Information
                </h2>
                {ageGroup.birth_date_min && ageGroup.birth_date_max && (
                  <p className="text-sm text-gray-500 mt-1">
                    Born {parseDateString(ageGroup.birth_date_min).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – {parseDateString(ageGroup.birth_date_max).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
              
              {sessions.length === 0 ? (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded mb-6">
                  <p className="text-amber-700">No placement sessions have been scheduled for this age group yet.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-indigo-500">
                  <div className="p-6">
                    <div className="space-y-6">
                      {sessions.map((session, sessionIndex) => (
                        <div key={sessionIndex} className="p-4 border border-gray-100 rounded-md bg-gray-50">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Session {sessionIndex + 1}</h4>
                          
                          <div className="space-y-3">
                            <div className="flex items-center text-gray-600">
                              <Calendar className="h-5 w-5 mr-2 text-indigo-500" />
                              <span>{session.date}</span>
                            </div>
                            
                            <div className="flex items-center text-gray-600">
                              <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                              <span>{session.time}</span>
                            </div>
                            
                            <div className="flex items-start text-gray-600">
                              <MapPin className="h-5 w-5 mr-2 text-indigo-500 mt-0.5" />
                              <div>
                                <p className="font-medium">{session.location.name}</p>
                                <p className="text-sm">{session.location.address}</p>
                              </div>
                            </div>
                            
                            {session.notes && (
                              <div className="mt-2 text-sm text-gray-600 italic">
                                {session.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      Register now to be part of our club and participate in these placements.{' '}
                      <Link to="/login" className="font-medium text-blue-700 underline hover:text-blue-600">
                        Register Here
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgeGroupTryoutInfo;
